import {
  BaseClient,
  BaseFile,
  BaseJobServiceLine,
  ClientRequestDetails,
  ClientRequestGroupModel,
  ClientRequestGroupType,
  ClientRequestMetadataModel,
  ClientRequestModel,
  DeliverableModel,
  StaffEntity,
} from '@aiwyn/api';
import { flattenIdentitiesToRequest, IdentitiesToRequest } from '@aiwyn/shared';
import { ClientRequestAssigneeModel, ClientRequestGroupIds, TypedIdentity } from '../models';

export interface ParsedIdentity<Type extends TypedIdentity> {
  id: number;
  type: ParseIdentityType<Type>;
}

type ParseIdentityType<T extends TypedIdentity> = T extends `${infer Type}-${number}` ? Type : never;

export const UNGROUPED_CLIENT_REQUEST_GROUP_ID = 0;

function isInEnum<Enum extends { [key: string]: Value }, Value>(enumType: Enum, value: Value): value is Enum[keyof Enum] {
  return Object.values(enumType).includes(value);
}

export const REQUEST_IDENTITY_TYPE = 'request';
export const DETAILS_IDENTITY_TYPE = 'details';
export const METADATA_IDENTITY_TYPE = 'metadata';
export const CLIENT_IDENTITY_TYPE = 'client';
export const CONTACT_IDENTITY_TYPE = 'contact';
export const STAFF_IDENTITY_TYPE = 'staff';
export const SERVICE_LINE_IDENTITY_TYPE = 'serviceLine';
export const DELIVERABLE_IDENTITY_TYPE = 'deliverable';
export const FILE_IDENTITY_TYPE = 'file';
export type RequestIdentityType = typeof REQUEST_IDENTITY_TYPE;
export type DetailsIdentityType = typeof DETAILS_IDENTITY_TYPE;
export type MetadataIdentityType = typeof METADATA_IDENTITY_TYPE;
export type ClientIdentityType = typeof CLIENT_IDENTITY_TYPE;
export type AssigneeIdentityType = typeof CONTACT_IDENTITY_TYPE; // | INVITEE_IDENTITY_TYPE;
export type StaffIdentityType = typeof STAFF_IDENTITY_TYPE;
export type ServiceLineIdentityType = typeof SERVICE_LINE_IDENTITY_TYPE;
export type DeliverableIdentityType = typeof DELIVERABLE_IDENTITY_TYPE;
export type FileIdentityType = typeof FILE_IDENTITY_TYPE;

export class ClientRequestIdentityHelper {
  private static splitIdentity<Type extends string = string>(identity: TypedIdentity<Type>): [Type, number] {
    if (!identity) {
      return [undefined, undefined];
    }

    const [type, id] = identity.split('-');
    return [type as Type, +id];
  }

  /**
   * Check if the id is valid. Must be a defined, valid, positive number. Zero is not valid by default.
   * @param id
   * @param isZeroValid
   * @private
   */
  private static isValidId(id: number, isZeroValid: boolean = false): boolean {
    return id != undefined && !Number.isNaN(id) && id > (isZeroValid ? -1 : 0);
  }

  /**
   * Get the identity string for the client request group.
   * If the group type is undefined, return 'ungrouped'.
   * @param group
   */
  static getGroupIdentity(group: ClientRequestGroupModel): TypedIdentity<ClientRequestGroupType> {
    if (!group) {
      return undefined;
    }

    return `${ClientRequestGroupType.JOB}-${group.job.id}`;
  }

  /**
   * Parse the identity string to get the job or engagement id.
   * @param identity
   */
  static parseGroupIdentity<Identity extends TypedIdentity<ClientRequestGroupType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id, true) && isInEnum(ClientRequestGroupType, type)
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  /**
   * Parse the identities to request and organize them by group type.
   * @param identities
   */
  static parseGroupIdentities(identities: TypedIdentity<ClientRequestGroupType>[]): ClientRequestGroupIds;
  static parseGroupIdentities(identities: IdentitiesToRequest<TypedIdentity<ClientRequestGroupType>>): ClientRequestGroupIds;
  static parseGroupIdentities(
    identities: TypedIdentity<ClientRequestGroupType>[] | IdentitiesToRequest<TypedIdentity<ClientRequestGroupType>>,
  ): ClientRequestGroupIds {
    const allIdentitiesToRequest = Array.isArray(identities) ? identities : flattenIdentitiesToRequest(identities);

    const parsedIdentities = allIdentitiesToRequest
      .map(identity => ClientRequestIdentityHelper.parseGroupIdentity(identity))
      .filter(Boolean);

    return parsedIdentities.reduce(
      (groupIds, parsedIdentity) => {
        return { ...groupIds, jobIds: (groupIds.jobIds ?? []).concat(parsedIdentity.id) };
      },
      {} as ClientRequestGroupIds,
    );
  }

  static getRequestIdentity(request: ClientRequestModel): TypedIdentity<RequestIdentityType> {
    return request?.id ? `${REQUEST_IDENTITY_TYPE}-${request.id}` : undefined;
  }

  static parseRequestIdentity<Identity extends TypedIdentity<RequestIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === REQUEST_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getDetailsIdentity(details: ClientRequestDetails): TypedIdentity<DetailsIdentityType> {
    return details?.clientRequestId ? `${DETAILS_IDENTITY_TYPE}-${details.clientRequestId}` : undefined;
  }

  static parseDetailsIdentity<Identity extends TypedIdentity<DetailsIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === DETAILS_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getMetadataIdentity(metadata: ClientRequestMetadataModel): TypedIdentity<MetadataIdentityType> {
    return metadata?.clientRequestId ? `${METADATA_IDENTITY_TYPE}-${metadata.clientRequestId}` : undefined;
  }

  static parseMetadataIdentity<Identity extends TypedIdentity<MetadataIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === METADATA_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getClientIdentity(client: BaseClient): TypedIdentity<ClientIdentityType> {
    return client?.id ? `${CLIENT_IDENTITY_TYPE}-${client.id}` : undefined;
  }

  static parseClientIdentity<Identity extends TypedIdentity<ClientIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === CLIENT_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getAssigneeIdentity(assignee: ClientRequestAssigneeModel): TypedIdentity<AssigneeIdentityType> {
    return assignee?.id ? `${CONTACT_IDENTITY_TYPE}-${assignee.id}` : undefined;
  }

  static parseAssigneeIdentity<Identity extends TypedIdentity<AssigneeIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === CONTACT_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getStaffIdentity(staff: StaffEntity): TypedIdentity<StaffIdentityType> {
    return staff?.id ? `${STAFF_IDENTITY_TYPE}-${staff.id}` : undefined;
  }

  static parseStaffIdentity<Identity extends TypedIdentity<StaffIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === STAFF_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getServiceLineIdentity(serviceLine: BaseJobServiceLine): TypedIdentity<ServiceLineIdentityType> {
    return serviceLine?.id ? `${SERVICE_LINE_IDENTITY_TYPE}-${serviceLine.id}` : undefined;
  }

  static parseServiceLineIdentity<Identity extends TypedIdentity<ServiceLineIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === SERVICE_LINE_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getDeliverableIdentity(deliverable: DeliverableModel): TypedIdentity<DeliverableIdentityType> {
    return deliverable?.id ? `${DELIVERABLE_IDENTITY_TYPE}-${deliverable.id}` : undefined;
  }

  static parseDeliverableIdentity<Identity extends TypedIdentity<DeliverableIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === DELIVERABLE_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

  static getFileIdentity(file: BaseFile): TypedIdentity<FileIdentityType> {
    return file?.id ? `${FILE_IDENTITY_TYPE}-${file.id}` : undefined;
  }

  static parseFileIdentity<Identity extends TypedIdentity<FileIdentityType>>(identity: Identity): ParsedIdentity<Identity> {
    const [type, id] = ClientRequestIdentityHelper.splitIdentity(identity);
    return (
      ClientRequestIdentityHelper.isValidId(id) && type === FILE_IDENTITY_TYPE
        ? { id, type }
        : undefined
    ) as ParsedIdentity<Identity>;
  }

}
